import React, { useEffect, useRef, useState } from 'react';
import PopUp from '../../../../../../muiComponents/PopUp';
import Cropper, { ReactCropperElement } from 'react-cropper';
import type CropperJS from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import useTranslate from '../../../../../../customHooks/useTranslate';
import {
    MIN_LOGO_HEIGHT,
    MIN_LOGO_WIDTH,
} from '../../../../utils/zitoneoServicesUtils';

const MAX_IMAGE_ZOOM = 1;

interface ImageCropPopUpProps {
    isOpen: boolean;
    file: File | null;
    onClose: () => void;
    onSave: (file: File) => Promise<void>;
    minWidth?: number;
    minHeight?: number;
    aspectRatio?: number;
    titleText?: string;
}
const ImageCropPopUp = ({
    isOpen,
    file,
    onClose,
    onSave,
    minWidth = MIN_LOGO_WIDTH,
    minHeight = MIN_LOGO_HEIGHT,
    aspectRatio,
    titleText = 'logo_crop',
}: ImageCropPopUpProps) => {
    const translate = useTranslate();
    const cropperRef = useRef<ReactCropperElement>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [finalImageSize, setFinalImageSize] = useState({
        width: 0,
        height: 0,
    });

    const updateFinalImageSize = (cropper: CropperJS) => {
        const cropData = cropper.getData(true);

        setFinalImageSize({
            width: cropData.width,
            height: cropData.height,
        });
    };

    const limitCanvasToNaturalSize = (cropper: CropperJS) => {
        const canvasData = cropper.getCanvasData();
        const containerData = cropper.getContainerData();
        const canvasScale = canvasData.width / canvasData.naturalWidth;

        if (canvasScale <= MAX_IMAGE_ZOOM) {
            return;
        }

        cropper.setCanvasData({
            left: (containerData.width - canvasData.naturalWidth) / 2,
            top: (containerData.height - canvasData.naturalHeight) / 2,
            width: canvasData.naturalWidth,
            height: canvasData.naturalHeight,
        });
    };

    const setMinimumCropBoxOptions = (cropper: CropperJS) => {
        const canvasData = cropper.getCanvasData();
        const imageScale = canvasData.width / canvasData.naturalWidth;
        const minCropBoxWidth = minWidth * imageScale;
        const minCropBoxHeight = minHeight * imageScale;

        const cropperWithOptions = cropper as CropperJS & {
            options: CropperJS.Options<HTMLImageElement>;
        };

        cropperWithOptions.options.minCropBoxWidth = minCropBoxWidth;
        cropperWithOptions.options.minCropBoxHeight = minCropBoxHeight;

        return { minCropBoxWidth, minCropBoxHeight };
    };

    const updateCropperConstraints = () => {
        const cropper = cropperRef.current?.cropper;

        if (!cropper) {
            return;
        }

        limitCanvasToNaturalSize(cropper);
        setMinimumCropBoxOptions(cropper);
        updateFinalImageSize(cropper);
    };

    const enforceMinimumCropSize = () => {
        const cropper = cropperRef.current?.cropper;

        if (!cropper) {
            return;
        }

        const cropData = cropper.getData();

        if (cropData.width >= minWidth && cropData.height >= minHeight) {
            updateFinalImageSize(cropper);
            return;
        }

        const imageData = cropper.getImageData();
        const width = Math.min(
            Math.max(cropData.width, minWidth),
            imageData.naturalWidth
        );
        const height = Math.min(
            Math.max(cropData.height, minHeight),
            imageData.naturalHeight
        );
        const x = Math.max(
            0,
            Math.min(cropData.x, imageData.naturalWidth - width)
        );
        const y = Math.max(
            0,
            Math.min(cropData.y, imageData.naturalHeight - height)
        );

        cropper.setData({
            x,
            y,
            width,
            height,
        });
        updateFinalImageSize(cropper);
    };

    const handleCrop = (event: CropperJS.CropEvent<HTMLImageElement>) => {
        updateFinalImageSize(event.currentTarget.cropper);
    };

    const handleZoom = (event: CropperJS.ZoomEvent<HTMLImageElement>) => {
        const cropper = event.currentTarget.cropper;
        const cropData = cropper.getData();
        const zoomScale = event.detail.oldRatio / event.detail.ratio;
        const nextCropWidth = cropData.width * zoomScale;
        const nextCropHeight = cropData.height * zoomScale;

        if (
            event.detail.ratio > MAX_IMAGE_ZOOM ||
            nextCropWidth < minWidth ||
            nextCropHeight < minHeight
        ) {
            event.preventDefault();
            return;
        }

        window.requestAnimationFrame(() => {
            limitCanvasToNaturalSize(cropper);
            setMinimumCropBoxOptions(cropper);
            updateFinalImageSize(cropper);
        });
    };

    const getCroppedLogoFile = async () => {
        const cropper = cropperRef.current?.cropper;

        if (!cropper || !file) {
            throw new Error('Logo cropper is not ready');
        }

        enforceMinimumCropSize();
        const cropData = cropper.getData(true);
        const canvas = cropper.getCroppedCanvas({
            width: cropData.width,
            height: cropData.height,
            minWidth,
            minHeight,
            maxWidth: cropper.getImageData().naturalWidth,
            maxHeight: cropper.getImageData().naturalHeight,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high',
        });

        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(croppedBlob => {
                if (croppedBlob) {
                    resolve(croppedBlob);
                    return;
                }

                reject(new Error('Logo crop could not be generated'));
            }, 'image/png');
        });
        const fileName = file.name.replace(/\.[^/.]+$/, '') || 'image';

        return new File([blob], `${fileName}.png`, { type: 'image/png' });
    };

    const handleSave = async () => {
        const croppedLogoFile = await getCroppedLogoFile();
        await onSave(croppedLogoFile);
    };

    useEffect(() => {
        if (!file) {
            setImageUrl(null);
            setFinalImageSize({ width: 0, height: 0 });
            return;
        }

        const url = URL.createObjectURL(file);
        setImageUrl(url);
        setFinalImageSize({ width: 0, height: 0 });

        return () => URL.revokeObjectURL(url);
    }, [file]);

    return (
        <PopUp
            titleText={titleText}
            open={isOpen}
            onClose={onClose}
            onSave={handleSave}
            onCancel={onClose}
            className='zitoneo-crop-pop-up'
        >
            {imageUrl && (
                <div className='zitoneo-crop__pop-up'>
                    <div className='zitoneo-crop__pop-up__container'>
                        <Cropper
                            key={imageUrl}
                            ref={cropperRef}
                            src={imageUrl}
                            className='zitoneo-crop'
                            viewMode={1}
                            dragMode='move'
                            guides
                            background={false}
                            responsive
                            restore={false}
                            checkOrientation={false}
                            autoCropArea={1}
                            cropBoxMovable
                            cropBoxResizable
                            aspectRatio={aspectRatio}
                            zoomable
                            scalable
                            zoomOnWheel
                            zoomOnTouch
                            toggleDragModeOnDblclick={false}
                            ready={updateCropperConstraints}
                            crop={handleCrop}
                            cropend={enforceMinimumCropSize}
                            zoom={handleZoom}
                        />
                    </div>
                    <p className='zitoneo-crop__final-size'>
                        {translate('final_image_size')}: {finalImageSize.width}{' '}
                        x {finalImageSize.height} px
                    </p>
                </div>
            )}
        </PopUp>
    );
};

export default ImageCropPopUp;
